import { X, Download, Trash2, Edit2, Check, FolderOpen, Clock, Workflow, Users, User, Star, StarOff } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth0UserId } from '../hooks/useAuth0UserId';
import { isAdminUser } from '../auth/auth0-config';
import * as flowApi from '../services/flowApi';

interface SavedFlow {
  id: string;
  name: string;
  nodes: any[];
  edges: any[];
  timestamp: string;
  metadata?: any;
  user_id?: string;  // Per mostrare l'owner quando admin vede tutti
  is_template?: boolean;  // True se è un template pubblico
}

interface FlowLibraryPanelProps {
  onClose: () => void;
  onLoadFlow: (flow: any) => void;  // Accetta qualsiasi formato di flow
}

export default function FlowLibraryPanel({ onClose, onLoadFlow }: FlowLibraryPanelProps) {
  const { user } = useAuth0();
  const userId = useAuth0UserId();
  const isAdmin = isAdminUser(user);
  const [savedFlows, setSavedFlows] = useState<SavedFlow[]>([]);
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [isLoading, setIsLoading] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);  // Solo admin
  const [togglingTemplateId, setTogglingTemplateId] = useState<string | null>(null);  // Flow in corso di toggle
  // isLoading è usato per mostrare stato di caricamento

  useEffect(() => {
    loadFlows();
  }, [userId, showAllUsers]);

  const loadFlows = async () => {
    setIsLoading(true);
    
    // Se autenticato, carica dal database
    if (userId) {
      try {
        const result = await flowApi.listFlows(userId, { 
          limit: 100,
          allUsers: isAdmin && showAllUsers,  // Admin può vedere tutti
          userEmail: user?.email,
        });
        const flows: SavedFlow[] = result.flows.map((f: any) => ({
          id: f.id,
          name: f.name,
          nodes: f.flow_data?.nodes || [],
          edges: f.flow_data?.edges || [],
          timestamp: f.updated_at || f.created_at,
          metadata: f.flow_data?.metadata,
          user_id: f.user_id,  // Per mostrare owner quando admin
          is_template: f.is_template || false,
        }));
        
        // Ordina
        flows.sort((a, b) => {
          if (sortBy === 'date') {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          } else {
            return a.name.localeCompare(b.name);
          }
        });
        
        setSavedFlows(flows);
      } catch (error) {
        console.error('Errore caricamento flows da database:', error);
        // Fallback a localStorage
        loadFlowsFromLocalStorage();
      } finally {
        setIsLoading(false);
      }
    } else {
      // Fallback a localStorage se non autenticato
      loadFlowsFromLocalStorage();
      setIsLoading(false);
    }
  };

  const loadFlowsFromLocalStorage = () => {
    const flows: SavedFlow[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('pigna-flow-')) {
        // Salta i prompt salvati (non sono flow)
        if (key === 'pigna-flow-generate-prompt' || 
            key === 'pigna-flow-promptify-prompt' ||
            key === 'pigna-flow-user-prompts') {
          continue;
        }
        
        const flowName = key.replace('pigna-flow-', '');
        const data = localStorage.getItem(key);
        
        if (data) {
          try {
            const parsed = JSON.parse(data);
            
            // Verifica che sia un flow valido (deve avere nodes e edges)
            if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
              console.warn(`⚠️ ${flowName} non è un flow valido (mancano nodes), skip`);
              continue;
            }
            
            flows.push({
              id: flowName,
              name: flowName,
              nodes: parsed.nodes || [],
              edges: parsed.edges || [],
              timestamp: parsed.timestamp || new Date().toISOString(),
              metadata: parsed.metadata,
            });
          } catch (e) {
            console.error(`❌ Errore parsing flow ${flowName}:`, e);
          }
        }
      }
    }
    
    // Ordina
    flows.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else {
        return a.name.localeCompare(b.name);
      }
    });
    
    setSavedFlows(flows);
  };

  const deleteFlow = async (flowId: string) => {
    const flow = savedFlows.find(f => f.id === flowId);
    if (!flow) return;
    
    if (confirm(`⚠️ Eliminare il flow "${flow.name}"?\n\nQuesta operazione è irreversibile.`)) {
      // Se autenticato, elimina dal database
      if (userId) {
        try {
          await flowApi.deleteFlow(flowId, userId);
          alert(`✅ Flow "${flow.name}" eliminato dal database!`);
          loadFlows();
        } catch (error: any) {
          console.error('Errore eliminazione flow:', error);
          alert(`❌ Errore eliminazione: ${error.message || 'Errore sconosciuto'}`);
          // Fallback a localStorage
          localStorage.removeItem(`pigna-flow-${flowId}`);
          loadFlows();
          alert(`⚠️ Flow eliminato da localStorage (fallback)`);
        }
      } else {
        // Fallback a localStorage se non autenticato
        localStorage.removeItem(`pigna-flow-${flowId}`);
        loadFlows();
        alert(`✅ Flow "${flow.name}" eliminato!`);
      }
    }
  };

  const startRename = (flow: SavedFlow) => {
    setEditingFlowId(flow.id);
    setEditingName(flow.name);
  };

  const saveRename = (oldId: string) => {
    if (!editingName.trim()) {
      alert('❌ Il nome non può essere vuoto');
      return;
    }
    
    // Carica flow vecchio
    const oldData = localStorage.getItem(`pigna-flow-${oldId}`);
    if (!oldData) return;
    
    try {
      const parsed = JSON.parse(oldData);
      parsed.id = editingName.trim();
      
      // Salva con nuovo nome
      localStorage.setItem(`pigna-flow-${editingName.trim()}`, JSON.stringify(parsed));
      
      // Elimina vecchio solo se nome diverso
      if (oldId !== editingName.trim()) {
        localStorage.removeItem(`pigna-flow-${oldId}`);
      }
      
      setEditingFlowId(null);
      setEditingName('');
      loadFlows();
      alert(`✅ Flow rinominato in "${editingName.trim()}"!`);
    } catch (e) {
      alert('❌ Errore durante il rinomina');
    }
  };

  const cancelRename = () => {
    setEditingFlowId(null);
    setEditingName('');
  };

  // Toggle template (solo admin)
  const toggleTemplate = async (flow: SavedFlow) => {
    if (!userId || !user?.email) return;
    
    setTogglingTemplateId(flow.id);
    const newIsTemplate = !flow.is_template;
    
    try {
      console.log(`🔄 Toggle template: ${flow.name} -> is_template=${newIsTemplate}`);
      
      await flowApi.updateFlow(flow.id, userId, {
        is_template: newIsTemplate,
      }, user.email);
      
      // Aggiorna localmente subito (senza aspettare loadFlows)
      setSavedFlows(prev => prev.map(f => 
        f.id === flow.id ? { ...f, is_template: newIsTemplate } : f
      ));
      
      console.log(`✅ Template toggled: ${flow.name} is now ${newIsTemplate ? 'TEMPLATE' : 'normale'}`);
      
      // Mostra feedback
      alert(newIsTemplate 
        ? `⭐ "${flow.name}" è ora un Template visibile a tutti!` 
        : `✅ "${flow.name}" non è più un template`);
        
    } catch (error) {
      console.error('❌ Errore toggle template:', error);
      alert('❌ Errore nel modificare lo stato template. Controlla la console per dettagli.');
    } finally {
      setTogglingTemplateId(null);
    }
  };

  const exportFlow = async (flow: SavedFlow) => {
    let flowData: string;
    
    // Se autenticato, carica dal database
    if (userId) {
      try {
        const dbFlow = await flowApi.getFlow(flow.id, userId);
        flowData = JSON.stringify(dbFlow.flow_data, null, 2);
      } catch (error) {
        console.error('Errore caricamento flow per export:', error);
        // Fallback a localStorage
        const localData = localStorage.getItem(`pigna-flow-${flow.id}`);
        if (!localData) {
          alert('❌ Flow non trovato');
          return;
        }
        flowData = localData;
      }
    } else {
      // Fallback a localStorage se non autenticato
      const localData = localStorage.getItem(`pigna-flow-${flow.id}`);
      if (!localData) {
        alert('❌ Flow non trovato');
        return;
      }
      flowData = localData;
    }
    
    const blob = new Blob([flowData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${flow.name}.pigna-flow.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    alert(`📥 Flow "${flow.name}" esportato!`);
  };

  const handleLoadFlow = async (flow: SavedFlow) => {
    // Se autenticato, carica dal database
    if (userId) {
      try {
        const dbFlow = await flowApi.getFlow(flow.id, userId);
        onLoadFlow(dbFlow.flow_data);
        onClose();
      } catch (error) {
        console.error('Errore caricamento flow da database:', error);
        // Fallback a localStorage
        const flowData = localStorage.getItem(`pigna-flow-${flow.id}`);
        if (!flowData) {
          alert('❌ Flow non trovato');
          return;
        }
        try {
          const parsed = JSON.parse(flowData);
          onLoadFlow(parsed);
          onClose();
        } catch (e) {
          alert('❌ Errore caricamento flow');
        }
      }
    } else {
      // Fallback a localStorage se non autenticato
      const flowData = localStorage.getItem(`pigna-flow-${flow.id}`);
      if (!flowData) {
        alert('❌ Flow non trovato');
        return;
      }
      try {
        const parsed = JSON.parse(flowData);
        onLoadFlow(parsed);
        onClose();
      } catch (e) {
        alert('❌ Errore caricamento flow');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b-2 border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FolderOpen className="w-7 h-7 text-purple-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Libreria Flow {isLoading && <span className="text-sm text-gray-400 ml-2">(caricamento...)</span>}
                </h2>
                <p className="text-sm text-gray-600">{savedFlows.length} flow salvati</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg transition"
              title="Chiudi"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Sort Controls */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Ordina per:</span>
            <button
              onClick={() => { setSortBy('date'); loadFlows(); }}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition ${
                sortBy === 'date' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-1" />
              Data
            </button>
            <button
              onClick={() => { setSortBy('name'); loadFlows(); }}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition ${
                sortBy === 'name' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Workflow className="w-4 h-4 inline mr-1" />
              Nome
            </button>
            
            {/* Admin Toggle - Vedi tutti i flow */}
            {isAdmin && (
              <div className="ml-4 pl-4 border-l-2 border-gray-300 flex items-center gap-2">
                <button
                  onClick={() => setShowAllUsers(!showAllUsers)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition flex items-center gap-1 ${
                    showAllUsers 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title="Admin: visualizza flow di tutti gli utenti"
                >
                  {showAllUsers ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  {showAllUsers ? 'Tutti gli utenti' : 'Solo i miei'}
                </button>
                <span className="text-xs text-red-600 font-bold">👑 Admin</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {savedFlows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 py-12">
              <FolderOpen className="w-20 h-20 mb-4 opacity-30" />
              <p className="text-xl font-bold">Nessun flow salvato</p>
              <p className="text-sm mt-2">Crea un flow e clicca "Salva" per aggiungerlo qui</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {savedFlows.map(flow => (
                <div
                  key={flow.id}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 hover:border-purple-400 shadow-md hover:shadow-xl transition-all p-4"
                >
                  {/* Header Flow Card */}
                  {editingFlowId === flow.id ? (
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-3 py-2 border-2 border-purple-400 rounded-lg font-bold text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename(flow.id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                      />
                      <button
                        onClick={() => saveRename(flow.id)}
                        className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition"
                        title="Salva"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={cancelRename}
                        className="p-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg transition"
                        title="Annulla"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-900 truncate flex-1">{flow.name}</h3>
                        {/* Badge Template */}
                        {flow.is_template && (
                          <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" /> Template
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {new Date(flow.timestamp).toLocaleString('it-IT')}
                      </p>
                      {/* Mostra owner se admin sta vedendo tutti i flow */}
                      {showAllUsers && flow.user_id && (
                        <p className="text-xs text-red-600 mt-1 font-semibold">
                          <Users className="w-3 h-3 inline mr-1" />
                          {flow.user_id.replace('auth0|', '').substring(0, 12)}...
                        </p>
                      )}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-1.5 text-blue-600">
                      <Workflow className="w-4 h-4" />
                      <span className="font-semibold">{flow.nodes.length}</span>
                      <span className="text-gray-500">nodi</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <span className="font-bold">→</span>
                      <span className="font-semibold">{flow.edges.length}</span>
                      <span className="text-gray-500">collegamenti</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLoadFlow(flow)}
                      className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold transition text-sm"
                    >
                      Carica
                    </button>
                    <button
                      onClick={() => exportFlow(flow)}
                      className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition"
                      title="Export JSON"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => startRename(flow)}
                      className="p-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition"
                      title="Rinomina"
                    >
                      <Edit2 size={18} />
                    </button>
                    {/* Admin: Toggle Template */}
                    {isAdmin && flow.user_id === userId && (
                      <button
                        onClick={() => toggleTemplate(flow)}
                        disabled={togglingTemplateId === flow.id}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          togglingTemplateId === flow.id
                            ? 'bg-gray-400 cursor-wait animate-pulse'
                            : flow.is_template 
                              ? 'bg-yellow-500 hover:bg-yellow-600 text-white ring-2 ring-yellow-300 shadow-lg' 
                              : 'bg-gray-300 hover:bg-yellow-400 text-gray-700 hover:text-white'
                        }`}
                        title={flow.is_template ? "⭐ TEMPLATE - Clicca per rimuovere" : "Rendi Template (visibile a tutti)"}
                      >
                        {togglingTemplateId === flow.id ? (
                          <span className="animate-spin">⏳</span>
                        ) : flow.is_template ? (
                          <Star size={18} className="fill-current" />
                        ) : (
                          <StarOff size={18} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => deleteFlow(flow.id)}
                      className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition"
                      title="Elimina"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            💡 <strong>Tip:</strong> Esporta i flow importanti per backup. Usa "Salva" nel canvas per creare nuovi flow.
          </p>
        </div>
      </div>
    </div>
  );
}

